/**
 * voice-v8 — Die Vorlese-Karte läuft mit, wenn ihre Antwort OHNE ihren Knopf gesprochen wird (21.10.2026).
 *
 * ## Der Anlass (Martin, 21:12, Bildschirmfoto)
 *
 * Die Sprechtasten-Antwort (1633 Zeichen) wurde vorgelesen — die Pille unten stand auf „Unterbrechen – ich spreche“, die
 * Bilanz um 21:13:52 sagt `wiedergabe(ptt) eingereiht=1618 gesprochen=1618`. Die Vorlese-Karte an derselben Antwort zeigte
 * dabei „Vorlesen · Teil 1 von 5“ mit Play-Zeichen, der erste Teil halb gefüllt (das ist ihr Ruhebild, `is-jetzt`). Martin:
 * „Sollte das Vorlesen nicht laufen?“ Die Karte liest ihren Zustand nur vom Vorlese-Knopf der Blase (vorlese-quelle.ts),
 * und den setzt nur dessen EIGENER Strom. Die Sprechtasten-Antwort (und jedes automatische Vorlesen) spielt in einer
 * Schlange ohne Knopf — die Karte blieb auf „ruht“, obwohl ihr Text klang.
 *
 * ## Was hier steht
 *
 * Spricht die SPRECHTASTEN-Schlange GENAU den Text einer Karte (dieselbe Frage wie beim Tipp auf den Knopf, gleiche-wiedergabe.ts),
 * bekommt deren Knopf `data-mitlauf="playing" | "paused"` und `data-mitlauf-bis` (gesprochene Zeichen). Die Quelle zeigt
 * das, solange der Knopf selbst ruht. Bedienung bleibt die bisherige: Tipp = Pause/Weiter der laufenden Antwort
 * (tippAufLaufendeAntwort), „Stopp“ = stopCurrentTts. Hier wird nichts gespielt, nichts abgebrochen, kein Mikro berührt.
 *
 * ⚠️ Nur Anzeige: `data-mitlauf-bis` wird nicht gemerkt (kein „Weiter ab …“ aus einem Mitlauf).
 * ⚠️ NUR die Sprechtasten-Schlange (Prüfer 21.10.): Der Tipp auf den Knopf nimmt nur sie als Pause/Weiter
 *    (app.ts makePlayButton → tippAufLaufendeAntwort(sprechtastenQueue, …)). Liefe die Karte auch bei einem automatischen
 *    Vorlesen einer getippten Antwort mit, zeigte sie „Pause“, und der Tipp bräche ab und begänne von vorn. Ebenso nie eine
 *    Schlange, die einem Knopf gehört — sonst zeigte eine zweite Blase mit gleichem Text „Liest vor“ und ihr Tipp bräche die
 *    erste ab. Die Sprechtasten-Schlange hat keinen Knopf (app.ts pttSendBlob).
 */
import { wirdGeradeGesprochen } from "./gleiche-wiedergabe.js";

/** Was der Mitlauf von einer Schlange wissen muss (TtsQueue erfüllt es). */
export interface MitlaufSchlange {
  isAudioActive(): boolean;
  isPaused(): boolean;
  sprichtText(): string;
  sprechBilanz(): { gesprochen: number };
}

interface MitlaufKnopf {
  knopf: HTMLButtonElement;
  /** Der Text, wie ihn eine Schlange für diese Antwort bekäme (app.ts: sprechQuelle(teileAntwort(text))). */
  sprech: () => string;
}

const knoepfe = new Set<MitlaufKnopf>();

/** So oft wird nachgesehen, solange eine Karte mitläuft (Fortschritt, Ende, Abbruch). */
export const MITLAUF_TAKT_MS = 1000;

/** Einen Vorlese-Knopf mit Karte anmelden. Abgehängte Knöpfe fallen beim nächsten Abgleich heraus. */
export function meldeMitlaufKnopf(knopf: HTMLButtonElement, sprech: () => string): void {
  let text: string | null = null;
  knoepfe.add({ knopf, sprech: () => (text ??= sprech()) });
}

function loesche(knopf: HTMLButtonElement): void {
  if (knopf.dataset.mitlauf !== undefined) delete knopf.dataset.mitlauf;
  if (knopf.dataset.mitlaufBis !== undefined) delete knopf.dataset.mitlaufBis;
}

/**
 * Jeden angemeldeten Knopf mit der Sprechtasten-Schlange abgleichen. Rückgabe: wie viele Karten gerade mitlaufen.
 * `melde` bekommt eine Zeile ohne Text, wenn eine Karte zu laufen beginnt.
 */
export function spiegleMitlauf(sprechtaste: MitlaufSchlange | null, melde?: (zeile: string) => void): number {
  // Früh: spricht die Sprechtaste nicht, läuft nichts mit (kein Sprechtext wird gebaut).
  const aktiv = sprechtaste?.isAudioActive() === true ? sprechtaste : null;
  let n = 0;
  for (const k of Array.from(knoepfe)) {
    if (!k.knopf.isConnected) {
      loesche(k.knopf);
      knoepfe.delete(k);
      continue;
    }
    // Der eigene Strom des Knopfs ist die Wahrheit — dann kein Mitlauf.
    const eigener = k.knopf.dataset.state ?? "idle";
    const s = eigener === "idle" && aktiv && wirdGeradeGesprochen(aktiv, k.sprech()) ? aktiv : null;
    if (!s) {
      loesche(k.knopf);
      continue;
    }
    n++;
    const zustand = s.isPaused() ? "paused" : "playing";
    if (k.knopf.dataset.mitlauf === undefined) melde?.(`[vorlesen] karte-mitlauf an zustand=${zustand}`);
    if (k.knopf.dataset.mitlauf !== zustand) k.knopf.dataset.mitlauf = zustand;
    const bis = String(Math.max(0, s.sprechBilanz().gesprochen));
    if (k.knopf.dataset.mitlaufBis !== bis) k.knopf.dataset.mitlaufBis = bis;
  }
  return n;
}

/**
 * Den Abgleich verdrahten: `anstossen()` nach jeder Änderung der Wiedergabe rufen (Pille, Stück-Start, Zug-Ende).
 * Solange eine Karte mitläuft, gleicht ein Takt nach — so verschwindet der Mitlauf auch nach einem Abbruch ohne Ereignis.
 */
export function erzeugeMitlauf(
  /** Die Schlange des laufenden Sprechtasten-Zuges (app.ts `sprechtastenQueue`) — keine andere, siehe oben. */
  sprechtaste: () => MitlaufSchlange | null,
  melde?: (zeile: string) => void,
): { anstossen(): void } {
  let takt: ReturnType<typeof setInterval> | null = null;
  const anstossen = (): void => {
    let n = 0;
    try {
      n = spiegleMitlauf(sprechtaste(), melde);
    } catch {
      /* eine Anzeige darf die Wiedergabe nie stören */
    }
    if (n > 0 && takt === null) takt = setInterval(anstossen, MITLAUF_TAKT_MS);
    else if (n === 0 && takt !== null) {
      clearInterval(takt);
      takt = null;
    }
  };
  return { anstossen };
}

/** Nur Tests. */
export function _leereMitlaufFuerTests(): void {
  knoepfe.clear();
}
